import React, { useState, useEffect } from "react";
import axios from "axios";
import { InputBox, UploadBox } from "../components/Input";
import StateManagedSelect from "react-select";
import LoadingSpinner from "../components/LoadingSpinner";
import { API_URL } from "../config";
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";
import { showToast } from "../components/toast.jsx";

const BroadcastModal = ({ isOpen, onClose, onSubmit, loading, checkingAll, selectedCount }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateList, setTemplateList] = useState([]);
    const [campaignName, setCampaignName] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [startLimit, setStartLimit] = useState("");
    const [endLimit, setEndLimit] = useState("");

    // Fetch WhatsApp templates from API when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const fetchWhatsappTemplates = async () => {
            try {
                const res = await axios.get(`${API_URL}settings/configurationsTemplates/getSubscriberBroadcastlist`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        Accept: "*/*",
                    },
                });
                if (res.data.data) {
                    setTemplateList(res.data.data);
                }
            } catch (err) {
                console.error("Error fetching WhatsApp templates:", err);
                showToast.error("Failed to load WhatsApp templates");
            }
        };

        fetchWhatsappTemplates();

        // Set default limits when checkingAll is true
        if (checkingAll && selectedCount) {
            setStartLimit("0");
            setEndLimit(selectedCount.toString());
        } else {
            setStartLimit("");
            setEndLimit("");
        }
    }, [isOpen, checkingAll, selectedCount]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setCampaignName("");
            setSelectedTemplate(null);
            setSelectedImage(null);
            setImagePreview(null);
            setStartLimit("");
            setEndLimit("");
        }
    }, [isOpen]);

    // Handle image upload
    const handleImageUpload = (file) => {
        if (!file) return;

        setSelectedImage(file);
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
    };

    // Clean up preview URL when component unmounts
    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!campaignName.trim()) {
            showToast.error("Please enter campaign name");
            return;
        }

        if (!selectedTemplate) {
            showToast.error("Please select a WhatsApp template");
            return;
        }

        if (!selectedImage) {
            showToast.error("Please upload an image");
            return;
        }

        // Validate limits if checkingAll is true
        if (checkingAll && startLimit && endLimit) {
            const start = parseInt(startLimit);
            const end = parseInt(endLimit);
            if (isNaN(start) || isNaN(end)) {
                showToast.error("Please enter valid numbers for limits");
                return;
            }
            if (start > end) {
                showToast.error("Start limit cannot be greater than end limit");
                return;
            }
        }

        // Prepare data in the same structure as the API expects
        const broadcastData = {
            campaign_name: campaignName,
            template_id: selectedTemplate.value,
            image: selectedImage,
            startLimit: checkingAll && startLimit ? startLimit : undefined,
            endLimit: checkingAll && endLimit ? endLimit : undefined,
        };

        onSubmit(broadcastData);
    };

    const handleStartLimitChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setStartLimit(value);
    };

    const handleEndLimitChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setEndLimit(value);
    };

    if (!isOpen) return null;

    const templateOptions = templateList.map((tpl) => ({
        label: tpl.templatename,
        value: tpl.id,
    }));

    // Calculate dynamic total records selected
    const calculatedCount =
        startLimit && endLimit && parseInt(endLimit) >= parseInt(startLimit)
            ? parseInt(endLimit) - parseInt(startLimit) + 1
            : selectedCount;

    return (
        <>
            <div className="modal-backdrop fade show"></div>
            <div
                className="modal fade show"
                style={{ display: "block" }}
                tabIndex="-1"
                role="dialog"
                aria-labelledby="broadcastModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="col-md-6 d-flex align-items-center">
                                <h5 className="modal-title" style={{ fontWeight: "bold", marginRight: "10px" }}>
                                    Send Broadcast
                                </h5>
                                <span
                                    style={{
                                        fontSize: "0.8rem",
                                        color: "#666",
                                        fontWeight: "normal"
                                    }}
                                    className="mt-1"
                                >
                                    ({checkingAll ? `All ${selectedCount}` : `${selectedCount}`} records selected)
                                </span>
                            </div>
                            <div className="col-md-6">
                                <button
                                    type="button"
                                    className="btn btn-default float-end"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="clearfix"></div>
                        </div>

                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Campaign Name */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <label className="control-label">Campaign Name*</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Enter campaign name"
                                                value={campaignName}
                                                onChange={(e) => setCampaignName(e.target.value)}
                                                maxLength={255}
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {/* WhatsApp Template Dropdown */}
                                    <div className="col-md-6 mb-3">
                                        <div className="form-group">
                                            <label className="control-label">WhatsApp Template*</label>
                                            <StateManagedSelect
                                                value={selectedTemplate}
                                                onChange={setSelectedTemplate}
                                                options={templateOptions}
                                                placeholder="-- Select WhatsApp Template --"
                                                isSearchable
                                                isDisabled={loading}
                                                required
                                                styles={{
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        maxHeight: 250,
                                                        overflowY: "auto",
                                                        overflowX: "hidden",
                                                    }),
                                                    control: (provided) => ({
                                                        ...provided,
                                                        opacity: loading ? 0.6 : 1,
                                                    })
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Start/End Limit Fields - show only if checkingAll is true */}
                                    {/* {checkingAll && (
                                        <>
                                            <div className="col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label className="control-label">Start Limit</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter start limit"
                                                        value={startLimit}
                                                        onChange={handleStartLimitChange}
                                                        maxLength={10}
                                                        disabled={loading}
                                                    />
                                                    <small className="form-text text-muted">
                                                        Optional: Start from record number (0-based)
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <div className="form-group">
                                                    <label className="control-label">End Limit</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter end limit"
                                                        value={endLimit}
                                                        onChange={handleEndLimitChange}
                                                        maxLength={10}
                                                        disabled={loading}
                                                    />
                                                    <small className="form-text text-muted">
                                                        Optional: End at record number
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="col-12 mb-3 text-end">
                                                <strong>Total Records Selected: {calculatedCount}</strong>
                                            </div>
                                        </>
                                    )} */}

                                    {/* Image Upload */}
                                    <div className="col-md-12 mb-3">
                                        <div className="form-group">
                                            <label className="control-label">Upload Image*</label>
                                            <input
                                                type="file"
                                                onChange={(e) => handleImageUpload(e.target.files[0])}
                                                accept="image/*"
                                                className="form-control"
                                                disabled={loading}
                                                required
                                            />
                                            {/* <small className="form-text text-muted">
                                                Supported formats: PNG, JPG, JPEG
                                            </small> */}

                                            {/* Image Preview */}
                                            {imagePreview && (
                                                <div className="mt-3">
                                                    <label className="control-label">Image Preview:</label>
                                                    <div className="mt-2">
                                                        <img
                                                            src={imagePreview}
                                                            alt="Preview"
                                                            style={{
                                                                maxWidth: "200px",
                                                                maxHeight: "200px",
                                                                border: "1px solid #ddd",
                                                                borderRadius: "4px",
                                                                padding: "4px"
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Submit Buttons */}
                                    <div className="col-md-12">
                                        <div style={{ float: "left", marginTop: "20px" }}>
                                            {hasPermission("bulk_broadcast", "add") ? (
                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className="btn btn-primary mx-2"
                                                    title="Submit broadcast"
                                                >
                                                    {loading ? "Sending..." : "Submit"}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary mx-2"
                                                    disabled
                                                    title="You don't have permission to send broadcasts"
                                                >
                                                    Submit
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={onClose}
                                                disabled={loading}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BroadcastModal;